import { breakpoints, Inline, Stack, Text, Tooltip } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { InformationIconButton } from '@eti/components';
import type { BoundType } from '../../models';
import BidirectionalSeparatedElements from '../../../../common/bidirectional-component/BidirectionalSeparatedElements';
import TextWithCopyButton from './TextWithCopyButton';

const inlineStyles = css`
  justify-content: space-between;
`;

const lightColoredTextStyles = css`
  color: #737373;
`;

interface PassengerTicketsProps {
  bound: BoundType;
  isContentReady?: boolean;
  passengerId?: string | null;
}

const ETickets = ({ passengerId, isContentReady = true, bound }: PassengerTicketsProps) => {
  const { t } = useTranslation();

  return (
    <Stack role="list">
      {bound.segments.map((segment) => {
        if (segment?.__typename === 'TripSegment') {
          const passengerSegmentTicket = segment.eTickets?.find(
            (ticket) => ticket.ticketNumber && ticket.travelerId === passengerId,
          );
          return (
            <Stack key={`${bound.boundId}-${segment.segmentId}-ticket`} role="listitem" spacing={8}>
              <Inline
                alignY="top"
                className={inlineStyles}
                collapseBelow={breakpoints._560}
                spacing={8}
              >
                <Stack spacing={0}>
                  <BidirectionalSeparatedElements
                    data-testid={`passenger-tickets-bound${bound.boundId}-segment${segment.segmentId}-carrier-and-flight`}
                    elements={[segment.marketingCarrier.name, segment.flightNumber]}
                  />
                  <BidirectionalSeparatedElements
                    className={lightColoredTextStyles}
                    data-testid={`passenger-tickets-bound${bound.boundId}-segment${segment.segmentId}-origin-and-destination`}
                    elements={[
                      `${segment.origin.cityName} (${segment.origin.code})`,
                      `${segment.destination.cityName} (${segment.destination.code})`,
                    ]}
                  />
                </Stack>
                {passengerSegmentTicket?.ticketNumber ? (
                  isContentReady && (
                    <TextWithCopyButton
                      data-testid={`passenger-tickets-bound${bound.boundId}-segment${segment.segmentId}-ticket-number-text`}
                      justifyContentVariant="flex-end"
                      shouldApplyMouseflowMask
                      text={passengerSegmentTicket.ticketNumber}
                    />
                  )
                ) : (
                  <Inline alignY="center" noWrap spacing={4}>
                    <Text
                      data-testid={`passenger-tickets-bound${bound.boundId}-segment${segment.segmentId}-not-available-text`}
                      key={`${passengerId}-${segment.flightNumber}`}
                    >
                      {t('OrderDetails.Passenger.Drawer.ETicket.NotAvailable.Text')}
                    </Text>
                    <Tooltip
                      content={t('OrderDetails.Passenger.Drawer.ETicket.NotAvailable.Tooltip')}
                      data-testid={`passenger-tickets-bound${bound.boundId}-segment${segment.segmentId}-not-available-tooltip`}
                      title={t('OrderDetails.Passenger.Drawer.ETicket.NotAvailable.Text')}
                    >
                      <InformationIconButton
                        data-testid={`passenger-tickets-bound${bound.boundId}-segment${segment.segmentId}-not-available-tooltip-icon`}
                      />
                    </Tooltip>
                  </Inline>
                )}
              </Inline>
            </Stack>
          );
        }
        return null;
      })}
    </Stack>
  );
};

export default ETickets;
